import { api } from "./api";

// Lightweight client-side activity tracker. Records page views + dwell time, every button
// click, and vault time/actions. Batches events and flushes periodically + on page hide.
// Never runs for the Super Admin console.

let sessionId = null;
let started = false;
let queue = [];
let flushTimer = null;
let lastPage = null;
let lastPageAt = 0;
let curVault = null;
let curVaultAt = 0;

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function push(type, name, meta) {
  if (!sessionId) return;
  queue.push({ type, name, meta: meta || {}, ts: new Date().toISOString() });
  if (queue.length >= 20) flush();
}

async function flush() {
  if (!sessionId || queue.length === 0) return;
  const events = queue;
  queue = [];
  try {
    await api.post("/activity/track", { session_id: sessionId, events });
  } catch {
    // best-effort; drop on failure to avoid unbounded growth
  }
}

function flushBeacon() {
  // Flush remaining events + end the session on page hide, using keepalive so it survives unload.
  if (!sessionId) return;
  // close out current page + vault dwell
  const now = Date.now();
  if (lastPage) { queue.push({ type: "page_view", name: lastPage, meta: { dwell_ms: now - lastPageAt }, ts: new Date().toISOString() }); lastPage = null; }
  if (curVault) { queue.push({ type: "vault_exit", name: curVault, meta: { tool: curVault, ms: now - curVaultAt }, ts: new Date().toISOString() }); curVault = null; }
  const token = localStorage.getItem("fv_token");
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const body = JSON.stringify({ session_id: sessionId, events: queue });
  queue = [];
  try {
    fetch(`${API_BASE}/activity/track`, { method: "POST", headers, body, keepalive: true });
    fetch(`${API_BASE}/activity/session/end`, { method: "POST", headers, body: JSON.stringify({ session_id: sessionId }), keepalive: true });
  } catch { /* ignore */ }
}

function onGlobalClick(e) {
  const el = e.target?.closest?.("button, [data-testid], [role='button'], a");
  if (!el) return;
  const name = el.getAttribute("data-testid") || el.getAttribute("aria-label")
    || (el.textContent || "").trim().slice(0, 48) || el.tagName.toLowerCase();
  push("click", name, { tag: el.tagName.toLowerCase(), path: window.location.pathname });
}

export async function initActivity(user) {
  if (started || !user || user.is_super_admin) return;
  started = true;
  try {
    const { data } = await api.post("/activity/session/start", {});
    if (!data.tracking) { started = false; return; }
    sessionId = data.session_id;
  } catch { started = false; return; }
  lastPage = window.location.pathname;
  lastPageAt = Date.now();
  document.addEventListener("click", onGlobalClick, true);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushBeacon(); });
  window.addEventListener("pagehide", flushBeacon);
  flushTimer = setInterval(flush, 8000);
}

export function trackPage(path) {
  if (!sessionId) return;
  const now = Date.now();
  if (lastPage && lastPage !== path) {
    push("page_view", lastPage, { dwell_ms: now - lastPageAt });
  }
  lastPage = path;
  lastPageAt = now;
}

export function trackVaultEnter(tool) {
  if (!sessionId) return;
  if (curVault) trackVaultExit();
  curVault = tool;
  curVaultAt = Date.now();
  push("vault_enter", tool, { tool });
}

export function trackVaultExit() {
  if (!sessionId || !curVault) return;
  push("vault_exit", curVault, { tool: curVault, ms: Date.now() - curVaultAt });
  curVault = null;
}

export function trackAction(tool, action, extra) {
  push("action", action, { tool, ...(extra || {}) });
}

export function endActivity() {
  if (!sessionId) { started = false; return; }
  flushBeacon();
  if (flushTimer) clearInterval(flushTimer);
  document.removeEventListener("click", onGlobalClick, true);
  sessionId = null;
  started = false;
  lastPage = null;
  curVault = null;
}
